import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Clock, ChevronDown, ChevronUp,
  Info, Activity, CheckCircle2, XCircle, HelpCircle,
  ArrowRight, Loader2, Lightbulb, FileText, Eye, Lock,
} from 'lucide-react';
import {
  validateQualificationResponse,
  VALID_LABELS, VALID_Q_STATES,
  LABEL_CONFIG, STATE_CONFIG,
} from '../lib/qualification';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── State types ─────────────────────────────────────────────────
const VIEW_STATE = { LOADING: 'loading', EMPTY: 'empty', ERROR: 'error', SUCCESS: 'success' };
const ERROR_TYPE = { NETWORK: 'network', SERVER: 'server', SCHEMA: 'invalid_schema' };

// ─── Main Component ──────────────────────────────────────────────

export const QualificationPanel = ({ qrId, sessionId }) => {
  const { token } = useAuth();
  const [viewState, setViewState] = useState(VIEW_STATE.LOADING);
  const [errorInfo, setErrorInfo] = useState(null);
  const [data, setData] = useState(null);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchQualification = useCallback(async () => {
    if (!qrId || !sessionId || !token) return;

    // Cancel previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const thisRequestId = ++requestIdRef.current;

    setViewState(VIEW_STATE.LOADING);
    setErrorInfo(null);

    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await axios.get(
          `${API}/qr-codes/${qrId}/qualification/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        );

        // Stale response guard (last-request-wins)
        if (thisRequestId !== requestIdRef.current) return;

        const d = res.data;
        const validation = validateQualificationResponse(d);
        if (!validation.valid) {
          console.error('[Qualification] Schema validation failed:', validation.errors);
          setViewState(VIEW_STATE.ERROR);
          setErrorInfo({ type: ERROR_TYPE.SCHEMA, details: validation.errors });
          return;
        }

        if (d.qualification_available === false) {
          setViewState(VIEW_STATE.EMPTY);
          setData(null);
          return;
        }

        setData(d);
        setViewState(VIEW_STATE.SUCCESS);
        return;
      } catch (err) {
        lastError = err;
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        if (attempt === 0) continue; // single retry
      }
    }

    // Both attempts failed
    if (thisRequestId !== requestIdRef.current) return;

    if (lastError?.response) {
      console.error('[Qualification] Server error:', lastError.response.status, lastError.response.data);
      setViewState(VIEW_STATE.ERROR);
      setErrorInfo({ type: ERROR_TYPE.SERVER, status: lastError.response.status });
    } else {
      console.error('[Qualification] Network error:', lastError?.message);
      setViewState(VIEW_STATE.ERROR);
      setErrorInfo({ type: ERROR_TYPE.NETWORK });
    }
  }, [qrId, sessionId, token]);

  useEffect(() => {
    fetchQualification();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchQualification]);

  // ─── Render states ──
  if (viewState === VIEW_STATE.LOADING) return <LoadingState />;
  if (viewState === VIEW_STATE.ERROR) return <ErrorState errorInfo={errorInfo} onRetry={fetchQualification} />;
  if (viewState === VIEW_STATE.EMPTY) return <EmptyStateWithInsights data={data} />;
  if (!data) return null;

  return <QualificationDisplay data={data} auditExpanded={auditExpanded} setAuditExpanded={setAuditExpanded} />;
};


// ─── Loading ─────────────────────────────────────────────────────

const LoadingState = () => (
  <div
    className="flex items-center gap-2 p-4 rounded-lg bg-[hsl(var(--muted))]"
    role="status"
    aria-live="polite"
    data-testid="qualification-loading"
  >
    <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--muted-foreground))]" aria-hidden="true" />
    <span className="text-sm text-[hsl(var(--muted-foreground))]">Loading qualification data...</span>
  </div>
);


// ─── Empty (no qualification, but may have insights) ────────────

const EmptyStateWithInsights = ({ data }) => {
  const insights = data?.insights;
  const insightsVersion = data?.insights_version;
  const isFallback = insights && insights.confidence === 0.0 && insights.summary === 'Insufficient data to generate insights.';
  const isLowConfidence = insights && !isFallback && insights.confidence < 0.4;
  const hasInsights = insights && typeof insights === 'object' &&
    typeof insights.summary === 'string' && typeof insights.confidence === 'number' &&
    Array.isArray(insights.evidence) && !isFallback;

  return (
    <div data-testid="qualification-empty">
      <div className="flex items-center gap-2 p-4 rounded-lg bg-[hsl(var(--muted))] mb-3">
        <Info className="h-4 w-4 text-[hsl(var(--muted-foreground))]" aria-hidden="true" />
        <span className="text-sm text-[hsl(var(--muted-foreground))]">
          No qualification score available for this session.
        </span>
      </div>
      {hasInsights && (
        <Card className="border border-[hsl(var(--border))] p-4">
          <InsightsSection insights={insights} isFallback={false} isLowConfidence={isLowConfidence} insightsVersion={insightsVersion} />
        </Card>
      )}
    </div>
  );
};


// ─── Error ───────────────────────────────────────────────────────

const ErrorState = ({ errorInfo, onRetry }) => {
  const messages = {
    [ERROR_TYPE.NETWORK]: 'Network error. Could not reach the server.',
    [ERROR_TYPE.SERVER]: `Server error (HTTP ${errorInfo?.status || '?'}).`,
    [ERROR_TYPE.SCHEMA]: 'Invalid response schema from server.',
  };

  return (
    <div
      className="p-4 rounded-lg bg-red-50 border border-red-200"
      role="alert"
      data-testid="qualification-error"
    >
      <div className="flex items-center gap-2 mb-2">
        <XCircle className="h-4 w-4 text-red-600" aria-hidden="true" />
        <span className="text-sm font-medium text-red-800">
          {messages[errorInfo?.type] || 'Unknown error'}
        </span>
      </div>
      {errorInfo?.type === ERROR_TYPE.SCHEMA && errorInfo?.details && (
        <ul className="mb-2 ml-6 list-disc text-xs text-red-700">
          {errorInfo.details.map((e, i) => <li key={`item-${i}`}>{e}</li>)}
        </ul>
      )}
      {errorInfo?.type === ERROR_TYPE.NETWORK && (
        <button
          onClick={onRetry}
          className="text-xs text-red-700 underline hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-400 rounded"
          data-testid="qualification-retry-btn"
        >
          Retry
        </button>
      )}
    </div>
  );
};


// ─── Success Display ─────────────────────────────────────────────

const QualificationDisplay = React.memo(({ data, auditExpanded, setAuditExpanded }) => {
  const labelConfig = LABEL_CONFIG[data.qualification_label] || null;
  const stateConfig = STATE_CONFIG[data.qualification_state] || null;
  const isKnownLabel = VALID_LABELS.includes(data.qualification_label);
  const isKnownState = VALID_Q_STATES.includes(data.qualification_state);

  const auditEvents = useMemo(() => {
    const events = data.audit_events || [];
    return [...events].reverse(); // latest first
  }, [data.audit_events]);

  const visibleAudit = auditExpanded ? auditEvents : auditEvents.slice(0, 20);
  const hasMoreAudit = auditEvents.length > 20;

  // Insights: present exactly as received, no transformation
  const insights = data.insights;
  const insightsLocked = data.insights_locked;
  const insightsUpgradeMessage = data.insights_upgrade_message;
  const insightsVersion = data.insights_version;
  const isFallback = insights && insights.confidence === 0.0 && insights.summary === 'Insufficient data to generate insights.';
  const isLowConfidence = insights && !isFallback && insights.confidence < 0.4;
  const hasInsights = insights && typeof insights === 'object' &&
    typeof insights.summary === 'string' && typeof insights.confidence === 'number' &&
    Array.isArray(insights.evidence);

  return (
    <Card
      className="border border-[hsl(var(--border))] overflow-hidden"
      data-testid="qualification-panel"
      role="region"
      aria-label="Qualification result"
    >
      {/* Header — Score + Label + State */}
      <div className="p-4 bg-[hsl(var(--muted))]">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-[hsl(var(--foreground))]" aria-hidden="true" />
            <span className="font-bold text-lg tabular-nums" data-testid="qualification-score">
              {data.qualification_score}<span className="text-sm font-normal text-[hsl(var(--muted-foreground))]">/100</span>
            </span>
            <LabelBadge label={data.qualification_label} known={isKnownLabel} config={labelConfig} />
            <StateBadge state={data.qualification_state} known={isKnownState} config={stateConfig} />
          </div>
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <span data-testid="qualification-config-version">v{data.config_version ?? '?'}</span>
            <span aria-hidden="true">|</span>
            <span data-testid="qualification-eval-timestamp">
              {data.evaluation_timestamp ? formatTimestamp(data.evaluation_timestamp) : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* AI Insights Section */}
        {insightsLocked ? (
          <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-3" data-testid="insights-locked">
            <Lock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">AI Insights — Paid Feature</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                {insightsUpgradeMessage || 'Upgrade to Starter or above to unlock AI-powered insights.'}
              </p>
              <a href="/billing" className="text-xs text-amber-700 dark:text-amber-300 underline hover:text-amber-900 mt-1.5 inline-block" data-testid="insights-upgrade-link">
                View Plans
              </a>
            </div>
          </div>
        ) : hasInsights ? (
          <InsightsSection insights={insights} isFallback={isFallback} isLowConfidence={isLowConfidence} insightsVersion={insightsVersion} />
        ) : (
          insights === undefined || insights === null ? (
            <div className="p-3 rounded-lg bg-[hsl(var(--muted))] text-sm text-[hsl(var(--muted-foreground))]" data-testid="insights-missing" role="status">
              <Info className="h-4 w-4 inline mr-1" aria-hidden="true" /> Insights not available.
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="insights-invalid" role="alert">
              <AlertTriangle className="h-4 w-4 inline mr-1" aria-hidden="true" /> Insights data has an unrecognized format.
            </div>
          )
        )}
        {(data.recommended_action || data.recommended_channel || data.urgency) && (
          <section aria-labelledby="qual-recommendations-heading">
            <h4 id="qual-recommendations-heading" className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
              Recommendation
            </h4>
            <div className="flex flex-wrap gap-2" data-testid="qualification-recommendations">
              {data.recommended_action && (
                <Badge variant="outline" className="gap-1 text-xs">
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  {formatAction(data.recommended_action)}
                </Badge>
              )}
              {data.recommended_channel && (
                <Badge variant="outline" className="gap-1 text-xs">
                  {formatChannel(data.recommended_channel)}
                </Badge>
              )}
              {data.urgency && (
                <UrgencyBadge urgency={data.urgency} />
              )}
            </div>
          </section>
        )}

        {/* Qualification Reasons */}
        {data.qualification_reasons && data.qualification_reasons.length > 0 && (
          <section aria-labelledby="qual-reasons-heading">
            <h4 id="qual-reasons-heading" className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
              Scoring Reasons ({data.qualification_reasons.length})
            </h4>
            <ul className="space-y-1" data-testid="qualification-reasons">
              {data.qualification_reasons.map((r, i) => (
                <li key={`item-${i}`} className="flex items-center gap-2 text-sm p-1.5 rounded bg-green-50/50 border border-green-100">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="font-medium">{r.label || r.rule_id}</span>
                    {' — '}
                    <span className="text-[hsl(var(--muted-foreground))]">{r.field_id}: "{r.answer}" {r.operator} "{r.expected}"</span>
                    <span className="ml-1 font-semibold text-green-700">+{r.points}pts</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Disqualification Reasons */}
        {data.disqualification_reasons && data.disqualification_reasons.length > 0 && (
          <section aria-labelledby="qual-disqualify-heading">
            <h4 id="qual-disqualify-heading" className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-2">
              Disqualification ({data.disqualification_reasons.length})
            </h4>
            <ul className="space-y-1" data-testid="qualification-disqualifications">
              {data.disqualification_reasons.map((r, i) => (
                <li key={`item-${i}`} className="flex items-center gap-2 text-sm p-1.5 rounded bg-red-50 border border-red-100">
                  <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="font-medium">{r.label || r.rule_id}</span>
                    {' — '}
                    <span className="text-[hsl(var(--muted-foreground))]">{r.field_id}: "{r.answer}" matched disqualify rule</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Missing Signals */}
        {data.missing_qualification_signals && data.missing_qualification_signals.length > 0 && (
          <section aria-labelledby="qual-missing-heading">
            <h4 id="qual-missing-heading" className="text-xs font-semibold uppercase tracking-wider text-yellow-700 mb-2">
              Missing Signals ({data.missing_qualification_signals.length})
            </h4>
            <ul className="space-y-1" data-testid="qualification-missing-signals">
              {data.missing_qualification_signals.map((s, i) => (
                <li key={`item-${i}`} className="flex items-center gap-2 text-sm p-1.5 rounded bg-yellow-50 border border-yellow-100">
                  <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 shrink-0" aria-hidden="true" />
                  <span>{s.field_id} — {s.label || 'required signal'} ({s.signal_type})</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Pending Confirmations */}
        {data.pending_qualification_confirmations && data.pending_qualification_confirmations.length > 0 && (
          <section aria-labelledby="qual-pending-heading">
            <h4 id="qual-pending-heading" className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">
              Pending Confirmations ({data.pending_qualification_confirmations.length})
            </h4>
            <ul className="space-y-1" data-testid="qualification-pending-confirmations">
              {data.pending_qualification_confirmations.map((p, i) => (
                <li key={`item-${i}`} className="flex items-center gap-2 text-sm p-1.5 rounded bg-blue-50 border border-blue-100">
                  <HelpCircle className="h-3.5 w-3.5 text-blue-600 shrink-0" aria-hidden="true" />
                  <span>{p.field_id}: "{p.value}" — pending ({p.confidence} confidence)</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Audit Trail */}
        <section aria-labelledby="qual-audit-heading">
          <div className="flex items-center justify-between">
            <h4 id="qual-audit-heading" className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
              Audit Trail ({auditEvents.length} events)
            </h4>
            {hasMoreAudit && (
              <button
                onClick={() => setAuditExpanded(prev => !prev)}
                className="flex items-center gap-1 text-xs text-[#1d4ed8] hover:underline focus:outline-none focus:ring-2 focus:ring-[#1d4ed8] rounded"
                aria-expanded={auditExpanded}
                aria-controls="audit-trail-list"
                data-testid="audit-expand-btn"
              >
                {auditExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {auditExpanded ? 'Show less' : `Show all ${auditEvents.length}`}
              </button>
            )}
          </div>
          <ul
            id="audit-trail-list"
            className="mt-2 space-y-1 max-h-64 overflow-y-auto"
            data-testid="qualification-audit-trail"
            role="list"
          >
            {visibleAudit.map((event, i) => (
              <AuditEventItem key={`item-${i}`} event={event} />
            ))}
            {auditEvents.length === 0 && (
              <li className="text-xs text-[hsl(var(--muted-foreground))] p-2">No audit events recorded.</li>
            )}
          </ul>
        </section>
      </div>
    </Card>
  );
});
QualificationDisplay.displayName = 'QualificationDisplay';


// ─── Sub-components ──────────────────────────────────────────────

const LabelBadge = ({ label, known, config }) => {
  if (!label) return null;
  if (!known) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-300"
        data-testid="qualification-label-badge"
        role="status"
      >
        {label}
        <span className="text-amber-600 text-[10px]" title="Unrecognized label">(unrecognized)</span>
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}
      data-testid="qualification-label-badge"
      role="status"
    >
      {label.charAt(0).toUpperCase() + label.slice(1)}
    </span>
  );
};

const StateBadge = ({ state, known, config }) => {
  if (!state) return null;
  if (!known) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300"
        data-testid="qualification-state-badge"
      >
        {state}
        <span className="text-amber-600 text-[10px]" title="Unrecognized state">(unrecognized)</span>
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${config.bg} ${config.text}`}
      data-testid="qualification-state-badge"
    >
      {config.label}
    </span>
  );
};

const UrgencyBadge = ({ urgency }) => {
  const colors = {
    high: 'bg-red-50 text-red-700 border-red-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-green-50 text-green-700 border-green-200',
  };
  return (
    <Badge variant="outline" className={`gap-1 text-xs ${colors[urgency] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
      <Activity className="h-3 w-3" aria-hidden="true" />
      {urgency || 'unknown'} urgency
    </Badge>
  );
};

const AuditEventItem = React.memo(({ event }) => {
  const ts = event.timestamp ? formatTimestamp(event.timestamp) : '—';
  const eventName = event.event || 'unknown';
  const ctx = event.context || {};

  let detail = '';
  if (eventName === 'qualification_computed') {
    detail = `score=${ctx.confirmed_only_score ?? '?'}, label=${ctx.qualification_label ?? '?'}, state=${ctx.qualification_state ?? '?'}`;
  } else if (eventName === 'rule_matched') {
    detail = `rule=${ctx.rule_id ?? '?'}, field=${ctx.field_id ?? '?'}, outcome=${ctx.outcome ?? '?'}, pts=${ctx.points ?? 0}`;
  } else if (eventName === 'score_changed') {
    detail = `${ctx.previous_score ?? '?'} -> ${ctx.new_score ?? '?'} (${ctx.previous_label ?? '?'} -> ${ctx.new_label ?? '?'})`;
  } else if (eventName === 'hard_disqualify') {
    detail = `rule=${ctx.rule_id ?? '?'}, field=${ctx.field_id ?? '?'}`;
  } else if (eventName === 'field_expired') {
    detail = `field=${ctx.field_id ?? '?'}`;
  } else if (eventName === 'conflict_rule_applied') {
    detail = `${ctx.conflict_rule_id ?? '?'}: capped to ${ctx.cap_value ?? '?'}`;
  } else {
    detail = JSON.stringify(ctx).slice(0, 120);
  }

  return (
    <li className="flex items-start gap-2 text-xs p-1.5 rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
      <Clock className="h-3 w-3 mt-0.5 text-[hsl(var(--muted-foreground))] shrink-0" aria-hidden="true" />
      <div>
        <span className="font-mono font-medium">{eventName}</span>
        <span className="text-[hsl(var(--muted-foreground))] ml-2">{ts}</span>
        {detail && <div className="text-[hsl(var(--muted-foreground))] mt-0.5">{detail}</div>}
      </div>
    </li>
  );
});
AuditEventItem.displayName = 'AuditEventItem';


// ─── Insights Section (read-only, no transformations) ────────────

const InsightsSection = React.memo(({ insights, isFallback, isLowConfidence, insightsVersion }) => {
  if (isFallback) {
    return (
      <div
        className="p-3 rounded-lg bg-[hsl(var(--muted))] text-sm text-[hsl(var(--muted-foreground))]"
        role="status"
        data-testid="insights-fallback"
      >
        <Info className="h-4 w-4 inline mr-1.5 -mt-0.5" aria-hidden="true" />
        <span>No insights available — insufficient data for analysis.</span>
        {insights.confidence_reason && <ConfidenceReasonTag reason={insights.confidence_reason} />}
        {insightsVersion && <span className="text-[10px] ml-2">(insights v{insightsVersion})</span>}
      </div>
    );
  }

  return (
    <section aria-labelledby="insights-heading" data-testid="insights-section">
      <div className="flex items-center justify-between mb-2">
        <h4 id="insights-heading" className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
          <Lightbulb className="h-3.5 w-3.5" aria-hidden="true" /> AI Insights
          {insightsVersion && <span className="text-[10px] font-normal">(v{insightsVersion})</span>}
        </h4>
        <ConfidenceReasonTag reason={insights.confidence_reason} />
        <ConfidenceBadge confidence={insights.confidence} isLow={isLowConfidence} />
      </div>

      {isLowConfidence && (
        <div
          className="flex items-center gap-1.5 mb-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800"
          role="alert"
          aria-live="polite"
          data-testid="insights-low-confidence"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Low confidence analysis — interpret with caution.
        </div>
      )}

      {/* Summary */}
      <div className="p-3 rounded-lg bg-[hsl(var(--muted))] mb-2" data-testid="insights-summary">
        <p className="text-sm">{insights.summary}</p>
      </div>

      {/* Recommended Action */}
      {insights.recommended_action && (
        <div className="flex items-center gap-1.5 mb-2" data-testid="insights-recommended-action">
          <ArrowRight className="h-3.5 w-3.5 text-[#1d4ed8]" aria-hidden="true" />
          <span className="text-sm font-medium">{insights.recommended_action}</span>
        </div>
      )}

      {/* Explanation */}
      {insights.explanation && (
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2 italic" data-testid="insights-explanation">
          {insights.explanation}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* Intent Signals */}
        {insights.intent_signals && insights.intent_signals.length > 0 && (
          <div data-testid="insights-intent-signals">
            <span className="text-[10px] font-semibold uppercase text-[hsl(var(--muted-foreground))]">Intent Signals</span>
            <ul className="mt-1 space-y-0.5" role="list">
              {insights.intent_signals.map((s, i) => (
                <li key={`item-${i}`} className="text-xs flex items-center gap-1 text-green-700">
                  <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden="true" /> {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pain Points */}
        {insights.pain_points && insights.pain_points.length > 0 && (
          <div data-testid="insights-pain-points">
            <span className="text-[10px] font-semibold uppercase text-[hsl(var(--muted-foreground))]">Pain Points</span>
            <ul className="mt-1 space-y-0.5" role="list">
              {insights.pain_points.map((p, i) => (
                <li key={`item-${i}`} className="text-xs flex items-center gap-1 text-red-700">
                  <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" /> {p}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Evidence */}
      {insights.evidence && insights.evidence.length > 0 && (
        <div className="mt-2" data-testid="insights-evidence">
          <span className="text-[10px] font-semibold uppercase text-[hsl(var(--muted-foreground))] flex items-center gap-1">
            <Eye className="h-3 w-3" aria-hidden="true" /> Evidence ({insights.evidence.length})
          </span>
          <ul className="mt-1 space-y-1" role="list">
            {insights.evidence.map((ev, i) => (
              <li key={`item-${i}`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
                <FileText className="h-3 w-3 text-[hsl(var(--muted-foreground))] shrink-0" aria-hidden="true" />
                <span>
                  <span className="font-mono text-[10px]">{ev.question_id}</span>
                  <span className="text-[hsl(var(--muted-foreground))]"> ({ev.field})</span>
                  {': '}
                  <span className="font-medium">"{ev.value}"</span>
                  <span className={`ml-1 text-[10px] ${ev.source === 'inferred' ? 'text-amber-600' : 'text-[hsl(var(--muted-foreground))]'}`}>
                    [{ev.source}]
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
});
InsightsSection.displayName = 'InsightsSection';

const ConfidenceBadge = ({ confidence, isLow }) => {
  if (confidence === null || confidence === undefined) return null;
  const pct = Math.round(confidence * 100);
  const color = isLow
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-green-50 text-green-700 border-green-200';
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${color}`}
      data-testid="insights-confidence"
      aria-label={`Confidence: ${pct}%`}
    >
      {pct}% confidence
    </span>
  );
};

const CONFIDENCE_REASON_LABELS = {
  low_data: 'Low Data',
  conflict: 'Conflict',
  weak_evidence: 'Weak Evidence',
  fallback: 'Fallback',
  normal: 'Normal',
};

const CONFIDENCE_REASON_COLORS = {
  low_data: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  conflict: 'bg-red-50 text-red-700 border-red-200',
  weak_evidence: 'bg-orange-50 text-orange-700 border-orange-200',
  fallback: 'bg-gray-50 text-gray-600 border-gray-200',
  normal: 'bg-green-50 text-green-700 border-green-200',
};

const ConfidenceReasonTag = ({ reason }) => {
  if (!reason) return null;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${CONFIDENCE_REASON_COLORS[reason] || 'bg-gray-50 text-gray-600 border-gray-200'}`}
      data-testid="insights-confidence-reason"
    >
      {CONFIDENCE_REASON_LABELS[reason] || reason}
    </span>
  );
};


// ─── Formatters ──────────────────────────────────────────────────

function formatTimestamp(ts) {
  if (!ts) return 'N/A';
  try {
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function formatAction(action) {
  const map = {
    schedule_demo: 'Schedule Demo',
    confirm_inferred_data: 'Confirm Inferred Data',
    gather_more_info: 'Gather More Info',
    send_materials: 'Send Materials',
    nurture_sequence: 'Nurture Sequence',
    not_a_fit: 'Not a Fit',
  };
  return map[action] || action;
}

function formatChannel(channel) {
  const map = { email: 'Email', call: 'Call', demo: 'Demo', nurture: 'Nurture' };
  return map[channel] || channel;
}
